import { forwardRef, useImperativeHandle } from 'react';

import { Box } from 'ui/box';
import { Stack } from 'ui/stack';

import Button from 'components/partials/button/button';
import { Loader } from 'ui/loader';
import TableComponent from 'components/partials/table-component/table-component';

import AddSpecialAwardModal from './components/add-special-award-modal';
import CalculationSettingsPanel from './components/calculation-settings-panel';
import { useCalculationsStep } from './hooks/use-calculations-step';

interface CalculationsStepProps {
  clientId?: string | number;
  readOnly?: boolean;
  onDirtyChange?: (dirty: boolean) => void;
}

const CalculationsStep = forwardRef<unknown, CalculationsStepProps>(
  ({ clientId: clientIdProp, readOnly, onDirtyChange }, ref) => {
    const {
      modelId,
      userId,
      clientId,
      calculation,
      settingsToRequest,
      settings,
      visibility,
      disabledGiftTargets,
      giftTargetOptions,
      grantCapOptions,
      setGiftTarget,
      setIncludeRequiredFees,
      setAdjustmentLoans,
      setAdjustmentWorkStudy,
      setCalculateNewNeed,
      setGrantCap,
      setGrantCapAmount,
      rules,
      rulesLoading,
      columns,
      isInitialLoading,
      hasChanges,
      reset,
      save,
      grantCapAmountError,
      isAwardModalOpen,
      editingRule,
      onOpenAddAward,
      onCloseAwardModal,
      onAwardSuccess,
    } = useCalculationsStep({ clientId: clientIdProp, onDirtyChange });

    useImperativeHandle(ref, () => ({ save, discard: reset, hasChanges }), [
      save,
      reset,
      hasChanges,
    ]);

    if (!modelId) {
      return (
        <Box sx={{ py: 4, textAlign: 'center', color: 'tokens.gray.600' }}>
          Please save the model first to configure calculations.
        </Box>
      );
    }

    return (
      <div
        className="relative"
        data-testid="calculations-step"
        inert={readOnly}
      >
        <Loader loading={isInitialLoading} fullScreen={false} />

        {!isInitialLoading && (
          <>
            <CalculationSettingsPanel
              settings={settings}
              visibility={visibility}
              giftTargetOptions={giftTargetOptions}
              grantCapOptions={grantCapOptions}
              disabledGiftTargets={disabledGiftTargets}
              onGiftTargetChange={setGiftTarget}
              onIncludeRequiredFeesChange={setIncludeRequiredFees}
              onAdjustmentLoansChange={setAdjustmentLoans}
              onAdjustmentWorkStudyChange={setAdjustmentWorkStudy}
              onCalculateNewNeedChange={setCalculateNewNeed}
              onGrantCapChange={setGrantCap}
              onGrantCapAmountChange={setGrantCapAmount}
              grantCapAmountError={grantCapAmountError}
            />

            <Box data-testid="special-awards-section" sx={{ mt: 4 }}>
              <Stack
                direction="row"
                sx={{
                  mb: 1.5,
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  gap: 2,
                }}
              >
                {!!rules.length && (
                  <h3 className="font-semibold text-gray-700">Special award</h3>
                )}

                <Button
                  type="button"
                  variant="reserve-outlined"
                  size="sm"
                  onClick={onOpenAddAward}
                  className="shadow"
                  data-testid="add-special-award-button"
                >
                  Add special award
                </Button>
              </Stack>

              {!!rules.length && (
                <Box sx={{ position: 'relative' }}>
                  <TableComponent
                    columns={columns}
                    data={rules}
                    isLoading={rulesLoading}
                    pagination={false}
                  />
                </Box>
              )}
            </Box>
          </>
        )}

        {isAwardModalOpen && userId && modelId && (
          <AddSpecialAwardModal
            // Remounts between add/edit so the modal starts from a clean form.
            key={editingRule?.id ?? 'new'}
            userId={userId}
            modelId={modelId}
            clientId={clientId ?? ''}
            calculation={calculation}
            settingsToRequest={settingsToRequest}
            rule={editingRule}
            onClose={onCloseAwardModal}
            onSuccess={onAwardSuccess}
          />
        )}
      </div>
    );
  }
);

CalculationsStep.displayName = 'CalculationsStep';

export default CalculationsStep;
